/**
 * Centralized Enterprise Logging Utility
 *
 * Environment-aware: logs only in lower environments (dev, int, uat). No logs in prod.
 * - prod: no console output
 * - dev / localhost: info, warn, error
 * - staging / int / uat: warn, error
 */

import { centralizedConfig } from './centralizedConfig';
import { getApiRequestTimeWarnThresholdMs } from './slowRequestThresholds';

// Log levels (higher number = more important). OFF = no logs.
const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  OFF: 4,
};

// Environment-based log level thresholds (prod = no logs)
const ENV_LOG_LEVELS = {
  dev: LOG_LEVELS.INFO,
  staging: LOG_LEVELS.WARN,
  prod: LOG_LEVELS.OFF,
  localhost: LOG_LEVELS.INFO,
};

// No styling - plain text only for professional logs

/** Plain object safe for formatMessage (Error serializes to {} with JSON.stringify). */
function normalizeLogContext(context) {
  if (context == null) return null;
  if (context instanceof Error) {
    const out = {
      errorName: context.name,
      errorMessage: context.message || String(context),
    };
    if (typeof context.stack === 'string' && context.stack.length > 0) {
      out.errorStackHead = context.stack.split('\n').slice(0, 3).join(' | ');
    }
    return out;
  }
  if (Array.isArray(context)) {
    return context.map((item) => (item instanceof Error ? normalizeLogContext(item) : item));
  }
  if (typeof context === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(context)) {
      out[k] = v instanceof Error ? normalizeLogContext(v) : v;
    }
    return out;
  }
  return context;
}

class Logger {
  constructor() {
    this.environment = null;
    this.branch = null;
    this.isLocalhost = false;
    this.minLevel = LOG_LEVELS.INFO; // Default fallback
    this.initialized = false;
    this.initPromise = this.initialize();
  }

  async initialize() {
    if (this.initialized) return;
    
    try {
      await centralizedConfig.waitForInitialization();
      this.environment = centralizedConfig.enviroment || '';
      this.branch = centralizedConfig.branch || 'main';
      this.isLocalhost = centralizedConfig.isRunningLocally();
      
      // Determine minimum log level based on environment
      const envKey = this.isLocalhost ? 'localhost' : 
                     this.environment === 'prod' ? 'prod' :
                     this.environment === 'staging' ? 'staging' : 'dev';
      
      this.minLevel = ENV_LOG_LEVELS[envKey] || LOG_LEVELS.INFO;
      this.initialized = true;
    } catch (error) {
      // Fallback to safe defaults if config not available
      this.environment = 'dev';
      this.minLevel = LOG_LEVELS.INFO;
      this.initialized = true;
    }
  }

  /**
   * Format log message with structured prefix
   * Format: [ENV][LEVEL][MODULE] message {context}
   */
  formatMessage(level, module, message, context = null) {
    const timestamp = new Date().toISOString().split('T')[1].slice(0, -1); // HH:MM:SS.mmm
    const envLabel = this.isLocalhost ? 'LOCAL' : 
                     this.environment?.toUpperCase() || 'DEV';
    const levelLabel = level.toUpperCase();
    const moduleLabel = module || 'APP';
    
    // Build plain text message - no styling
    let fullMessage = `[${envLabel}][${levelLabel}][${moduleLabel}] ${timestamp} ${message}`;
    
    // Add context if provided
    if (context && typeof context === 'object') {
      const contextStr = Object.entries(context)
        .map(([key, value]) => {
          if (typeof value === 'object') {
            try {
              return `${key}: ${JSON.stringify(value)}`;
            } catch {
              return `${key}: [object]`;
            }
          }
          return `${key}: ${value}`;
        })
        .join(', ');
      fullMessage += ` {${contextStr}}`;
    }
    
    return { fullMessage };
  }

  /**
   * Check if context is suitable for table display
   */
  shouldUseTable(context) {
    if (!context) return false;
    
    // Use table for arrays (especially arrays of objects)
    if (Array.isArray(context)) {
      return context.length > 0 && context.length <= 100; // Limit to 100 rows for performance
    }
    
    // Use table for objects with multiple properties (but not too many)
    if (typeof context === 'object' && context !== null) {
      const keys = Object.keys(context);
      // Use table if it's an object with multiple properties that could benefit from tabular display
      // But skip if it's a simple key-value pair (better as inline context)
      return keys.length > 3 && keys.length <= 20;
    }
    
    return false;
  }

  /**
   * Core logging method
   */
  async log(level, module, message, context = null) {
    // Ensure initialized
    if (!this.initialized) {
      await this.initPromise;
    }

    const normalizedContext = normalizeLogContext(context);
    
    const levelNum = LOG_LEVELS[level.toUpperCase()] ?? LOG_LEVELS.INFO;
    
    // Filter by environment-based minimum level
    if (levelNum < this.minLevel) {
      return;
    }
    
    const { fullMessage } = this.formatMessage(level, module, message, null); // Don't include context in message if using table
    
    // Check if we should use console.table for structured data
    const useTable = this.shouldUseTable(normalizedContext);
    
    // Route to appropriate console method
    switch (level.toUpperCase()) {
      case 'ERROR':
        if (useTable) {
          console.error(fullMessage);
          console.table(normalizedContext);
        } else {
          const { fullMessage: msgWithContext } = this.formatMessage(level, module, message, normalizedContext);
          console.error(msgWithContext);
        }
        break;
      case 'WARN':
        if (useTable) {
          console.warn(fullMessage);
          console.table(normalizedContext);
        } else {
          const { fullMessage: msgWithContext } = this.formatMessage(level, module, message, normalizedContext);
          console.warn(msgWithContext);
        }
        break;
      case 'DEBUG':
        if (useTable) {
          console.debug(fullMessage);
          console.table(normalizedContext);
        } else {
          const { fullMessage: msgWithContext } = this.formatMessage(level, module, message, normalizedContext);
          console.debug(msgWithContext);
        }
        break;
      case 'INFO':
      default:
        if (useTable) {
          console.log(fullMessage);
          console.table(normalizedContext);
        } else {
          const { fullMessage: msgWithContext } = this.formatMessage(level, module, message, normalizedContext);
          console.log(msgWithContext);
        }
        break;
    }
  }

  // Convenience methods
  async debug(module, message, context) {
    return this.log('DEBUG', module, message, context);
  }

  async info(module, message, context) {
    return this.log('INFO', module, message, context);
  }

  async warn(module, message, context) {
    return this.log('WARN', module, message, context);
  }

  async error(module, message, context) {
    return this.log('ERROR', module, message, context);
  }

  /**
   * Performance timing helper
   */
  async time(module, label, startTime, context = null) {
    if (!this.initialized) await this.initPromise;
    
    const duration = performance.now() - startTime;
    const ep = context && typeof context.endpoint === 'string' ? context.endpoint : '';
    const warnThresholdMs = getApiRequestTimeWarnThresholdMs(ep);
    const level = duration > warnThresholdMs ? 'WARN' : 'INFO';
    
    return this.log(level, module, `${label}: ${duration.toFixed(2)}ms`, {
      ...context,
      duration_ms: duration.toFixed(2)
    });
  }
}

// Create singleton instance
const loggerInstance = new Logger();

// Export convenience functions that handle async initialization
export const logger = {
  debug: (module, message, context) => loggerInstance.debug(module, message, context),
  info: (module, message, context) => loggerInstance.info(module, message, context),
  warn: (module, message, context) => loggerInstance.warn(module, message, context),
  error: (module, message, context) => loggerInstance.error(module, message, context),
  time: (module, label, startTime, context) => loggerInstance.time(module, label, startTime, context),
};

export default logger;

